import { limitar, vetor, type Vetor3 } from '../geometria/vetor3'
import type { TipoAlvoImpacto } from './alvosImpacto'
import type { NivelDanoVeicular, RegiaoDanoVeicular } from './danoVeicular'
import {
  amostrarDeformacao,
  type DescritorDeformacaoVeicular,
  type VarianteDeformacao,
} from './deformacaoVeicular'
import { raioArcoRoda, type PerfilVeiculo } from './perfilVeiculo'

/**
 * Campo de deformação didático: UMA função que diz para onde cada ponto do
 * veículo vai depois do impacto, no referencial do próprio veículo.
 *
 * É o que mantém as peças coerentes entre si. Capô, para-choque, para-lama,
 * longarina e farol não têm cada um a sua regra solta: todos consultam o
 * mesmo campo, cada um pela sua "camada" (a chapa do capô dobra para cima, a
 * longarina flamba para o lado, o para-choque cede para baixo), e por isso a
 * frente inteira encurta junto.
 *
 * Não é mecânica estrutural. É uma composição de funções simples —
 * perfil transversal (plano, em V, com degrau), compressão longitudinal,
 * vincos por distância a uma linha — escolhidas para que muro, poste e outro
 * veículo produzam assinaturas diferentes e legíveis. TypeScript puro: não
 * conhece Three.js; a cena aplica o resultado nos vértices.
 */

/**
 * Como a peça responde ao campo.
 *
 * - `paraChoque`: fáscia; recua inteira, cede para baixo na frente e sobe na
 *   traseira (o veículo que atinge mergulha por baixo).
 * - `capo`: chapa horizontal da frente; encurta em sanfona, com vincos.
 * - `tampaTraseira`: tampa/porta-malas; entra por baixo e dobra em 1–2 vincos.
 * - `teto`: painel do teto.
 * - `lateral`: chapa lateral fora das portas (para-lamas, caixas de roda).
 * - `porta`: chapa externa da porta; tem barra de proteção e vincos diagonais.
 * - `estrutura`: longarinas, colunas, soleiras — flambam e têm limite de intrusão.
 * - `rigida`: peça que não deforma, só é deslocada (farol, lanterna, roda).
 */
export type CamadaDeformacao =
  | 'paraChoque'
  | 'capo'
  | 'tampaTraseira'
  | 'teto'
  | 'lateral'
  | 'porta'
  | 'estrutura'
  | 'rigida'

/** Um impacto já amostrado num instante: o descritor com a intrusão atual. */
export interface CampoDeformacao {
  regiao: RegiaoDanoVeicular
  alvo: TipoAlvoImpacto
  variante: VarianteDeformacao
  nivel: NivelDanoVeicular
  /** intrusão neste instante, em metros */
  intrusaoM: number
  intrusaoMaxM: number
  /** 0..1 */
  progresso: number
  larguraM: number
  centroFaceM: number
  /** Somente para amassados locais no teto. */
  centroLongitudinalM?: number
  localizado?: boolean
  alturaContatoY: number
  sinalLateral: 1 | -1
  smallOverlap: boolean
  assimetria: number
  /** contato tangencial: riscos ao longo da área de contato */
  riscos: boolean
}

/** Deformação de UM veículo num instante: um campo por impacto sofrido. */
export interface EstadoDeformacaoVeiculo {
  veiculoId: string
  campos: readonly CampoDeformacao[]
  /**
   * Assinatura estável do estado, para memorização. Dois instantes com a
   * mesma chave produzem a mesma geometria — depois que o esmagamento termina,
   * a malha para de ser recalculada.
   */
  chave: string
}

const ZERO = vetor(0, 0, 0)

const suave = (a: number, b: number, x: number): number => {
  if (b === a) return x < a ? 0 : 1
  const t = limitar((x - a) / (b - a), 0, 1)
  return t * t * (3 - 2 * t)
}

/**
 * Perfil de um vinco: 1 sobre a linha de dobra, caindo a 0 em `largura`.
 *
 * A tenda elevada a 1,5 dá aresta viva no centro e cauda suave — é a aresta
 * que faz a chapa parecer DOBRADA em vez de empurrada por uma bola.
 */
export function vinco(distancia: number, largura: number): number {
  const t = Math.max(0, 1 - Math.abs(distancia) / Math.max(largura, 1e-6))
  return t * Math.sqrt(t)
}

/**
 * Quanto do esmagamento chega a cada ponto ao longo da face atingida.
 *
 * - muro: platô largo, quase a face inteira, com pequena irregularidade para
 *   não virar um recorte perfeito;
 * - poste: V linear com núcleo estreito — fundo numa faixa pequena, sem
 *   formato circular;
 * - outro veículo: platô do tamanho da sobreposição, bordas mais duras e
 *   inclinação quando o contato é assimétrico.
 */
export function pesoTransversal(
  alvo: TipoAlvoImpacto,
  u: number,
  meiaLargura: number,
  assimetria = 0,
): number {
  const hw = Math.max(meiaLargura, 0.05)
  const a = Math.abs(u)
  if (alvo === 'poste') {
    const v = Math.max(0, 1 - a / (hw * 2.4))
    const nucleo = Math.exp(-((u / (hw * 0.5)) ** 2))
    return limitar(0.52 * v + 0.48 * nucleo, 0, 1)
  }
  if (alvo === 'muro') {
    const plato = 1 - suave(hw * 0.92, hw * 1.3, a)
    const irregular = 0.93 + 0.07 * Math.sin(u * 6.1 + 0.8)
    return plato * irregular
  }
  const plato = 1 - suave(hw * 0.7, hw * 1.25, a)
  const inclinacao = 1 + 0.35 * assimetria * limitar(u / hw, -1, 1)
  return limitar(plato * inclinacao, 0, 1.2)
}

/**
 * Onde, ao longo da profundidade normalizada `s`, fica a linha de um vinco
 * neste ponto da largura. É isso que dá a DIREÇÃO das dobras:
 * transversais e levemente curvas no muro, em V apontando para o poste,
 * diagonais no contato entre veículos e no small overlap.
 */
function desvioDoVinco(c: CampoDeformacao, u: number, x: number, meiaLarguraVeiculo: number): number {
  const hw = c.larguraM / 2
  if (c.smallOverlap) return 0.3 * limitar((x * c.sinalLateral) / meiaLarguraVeiculo, -1, 1)
  if (c.alvo === 'poste') return 0.32 * Math.min(1, Math.abs(u) / (hw * 3))
  if (c.alvo === 'muro') return 0.05 * limitar(u / hw, -1.3, 1.3) ** 2
  const sentido = c.assimetria === 0 ? 1 : Math.sign(c.assimetria)
  return 0.16 * sentido * limitar(u / meiaLarguraVeiculo, -1, 1)
}

// Vincos do capô: posição na zona esmagada (1 = borda de ataque) e sentido.
// Alternar para cima e para baixo é o que forma a sanfona.
const VINCOS_CAPO = [
  { s: 0.82, amplitude: 1 },
  { s: 0.55, amplitude: -0.3 },
  { s: 0.28, amplitude: 0.75 },
] as const

const VINCOS_TAMPA = [
  { s: 0.72, amplitude: 1 },
  { s: 0.36, amplitude: 0.5 },
] as const

/**
 * Frente e traseira: compressão longitudinal da zona de absorção.
 *
 * `sentido` = +1 na frente (a face está em `frenteZ` e recua para −Z) e −1 na
 * traseira. A traseira NÃO é a frente espelhada: o para-choque sobe em vez de
 * ceder, a tampa entra por baixo, o piso do porta-malas pode levantar e há
 * menos vincos na chapa de cima.
 */
function deslocamentoLongitudinal(
  c: CampoDeformacao,
  perfil: PerfilVeiculo,
  p: Vetor3,
  camada: CamadaDeformacao,
): Vetor3 {
  const frente = c.regiao === 'frente'
  const sentido = frente ? 1 : -1
  const I = c.intrusaoM
  const zFace = frente ? perfil.frenteZ : perfil.traseiraZ
  // limite do habitáculo: a zona de absorção termina antes da coluna A / do banco traseiro
  const zLimite = frente ? perfil.colunaA.zBase + 0.06 : perfil.colunaC.zBase - 0.05
  const alcance = Math.abs(zFace - zLimite)
  const comprimentoEsmagamento = limitar(I * 2.3 + 0.25, 0.3, alcance)
  const profundidade = sentido * (zFace - p.z)
  const s = limitar(1 - profundidade / comprimentoEsmagamento, 0, 1)
  const hwV = perfil.largura / 2
  const u = p.x - c.centroFaceM
  const hw = c.larguraM / 2

  let dx = 0
  let dy = 0
  let dz = 0

  if (s > 0) {
    const wFace = pesoTransversal(c.alvo, u, hw, c.assimetria)
    // a carga se espalha para dentro: longe da face o perfil é mais largo
    const wLarga = pesoTransversal(c.alvo === 'poste' ? 'outroVeiculo' : c.alvo, u, hw * 1.8, c.assimetria)
    const w = wLarga + (wFace - wLarga) * s
    const g = Math.pow(s, 1.1)
    dz = -sentido * I * w * g

    const xr = limitar(p.x / hwV, -1.2, 1.2)
    if (c.alvo === 'muro') {
      // cantos se abrem: a peça comprimida expande para os lados
      dx += Math.sign(p.x) * I * 0.07 * w * s * xr * xr
    } else if (c.alvo === 'poste') {
      // o que está perto do poste é arrastado para ele: peças convergem
      dx += -Math.sign(u) * I * 0.2 * Math.max(0, 1 - Math.abs(u) / (hw * 3.5)) * s
    } else {
      dx += I * 0.12 * c.assimetria * s * w
    }
    if (c.smallOverlap) dx += c.sinalLateral * I * 0.08 * w * s

    const torcao = c.smallOverlap ? c.sinalLateral : c.alvo === 'outroVeiculo' ? c.assimetria : 0

    switch (camada) {
      case 'capo': {
        const wElev = limitar(0.35 + 0.65 * w, 0, 1.2)
        const desvio = desvioDoVinco(c, u, p.x, hwV)
        for (const v of VINCOS_CAPO) {
          dy += I * 0.42 * v.amplitude * vinco(s - (v.s + desvio), 0.13) * wElev
        }
        // o capô sobe como uma tenda larga entre os vincos
        dy += I * 0.1 * w * Math.sin(Math.PI * s)
        // torção: um lado sobe, o outro desce
        dy += I * 0.12 * torcao * xr * s
        break
      }
      case 'tampaTraseira': {
        // o sedan tem tampa horizontal; o hatch, quase vertical — a dobra sai pela normal da peça
        const hatch = perfil.volumeTraseiro === 'hatch'
        const ny = hatch ? 0.45 : 1
        const nz = hatch ? -0.89 : 0
        const desvio = desvioDoVinco(c, u, p.x, hwV)
        let dobra = 0
        for (const v of VINCOS_TAMPA) dobra += 0.3 * v.amplitude * vinco(s - (v.s + desvio), 0.15)
        // a borda de baixo da tampa entra mais (é o que o para-choque de quem bate encontra)
        const entrada = hatch ? 0.12 * (1 - suave(perfil.cinturaY - 0.3, perfil.cinturaY, p.y)) : 0
        dy += I * w * (dobra * ny)
        dz += I * w * (dobra * nz) + sentido * -I * entrada * w
        dy += I * 0.1 * torcao * xr * s
        break
      }
      case 'paraChoque': {
        dy += frente ? -I * 0.06 * w * s : I * 0.11 * w * s
        // a fáscia dobra ao meio na horizontal (entra mais no meio da altura)
        // e enruga em pregas verticais irregulares — não é uma placa recuada
        const meioAltura = 0.26 + (perfil.capoY - 0.26) * (frente ? 0.5 : 0.42)
        dz += -sentido * I * 0.14 * w * vinco(p.y - meioAltura, 0.16) * s
        dz += -sentido * I * 0.05 * w * s * (0.5 + 0.5 * Math.sin(p.x * 13.7 + 0.6)) * (c.alvo === 'poste' ? 0.4 : 1)
        break
      }
      case 'lateral':
        // para-lama: onda que aproxima da roda e dobra para fora e para cima
        dy += I * 0.08 * w * Math.sin(Math.PI * s)
        dx += Math.sign(p.x) * I * 0.09 * w * Math.sin(2 * Math.PI * s)
        break
      case 'estrutura': {
        // flambagem: a longarina não encurta reta, ela dobra
        dx += Math.sign(p.x) * I * 0.16 * w * Math.sin(Math.PI * s)
        dy += I * 0.07 * w * Math.sin(2 * Math.PI * s)
        if (!frente && p.y < perfil.pisoY + 0.2 && I > 0.35) {
          // piso do porta-malas levanta em traseira severa
          dy += (I - 0.35) * 0.5 * w * s
        }
        break
      }
      default:
        break
    }
  }

  // Small overlap: a longarina é contornada e a roda vira aríete — um pouco da
  // intrusão passa para a caixa de roda e a base da coluna A, só daquele lado
  // e só abaixo da cintura. É a única exceção à cabine preservada na frente.
  if (c.smallOverlap && frente && p.z < zLimite + 0.05) {
    const lado = suave(0.25, 0.75, (p.x * c.sinalLateral) / hwV)
    const baixo = 1 - suave(perfil.cinturaY - 0.1, perfil.cinturaY + 0.15, p.y)
    const atras = limitar(1 - (zLimite + 0.05 - p.z) / 0.55, 0, 1)
    dz += -Math.min(0.14, I * 0.3) * lado * baixo * atras
  }

  return vetor(dx, dy, dz)
}

/** Altura em que a lateral mais cede: pico no para-choque de quem bate, soleira e teto resistem. */
function pesoVertical(perfil: PerfilVeiculo, y: number, yContato: number, alvo: TipoAlvoImpacto): number {
  const yc = limitar(yContato, perfil.topoSoleiraY + 0.06, perfil.cinturaY - 0.08)
  const pontos: Array<[number, number]> =
    alvo === 'poste'
      ? [
          [perfil.pisoY - 0.1, 0.4],
          [perfil.topoSoleiraY, 0.55],
          [yc, 1],
          [perfil.cinturaY, 0.9],
          [perfil.tetoY, 0.4],
        ]
      : [
          [perfil.pisoY - 0.1, 0.12],
          [perfil.topoSoleiraY - 0.02, 0.3],
          [yc, 1],
          [perfil.cinturaY, 0.6],
          [(perfil.cinturaY + perfil.tetoY) / 2, 0.28],
          [perfil.tetoY, 0.12],
        ]
  if (y <= pontos[0]![0]) return pontos[0]![1]
  for (let i = 0; i < pontos.length - 1; i += 1) {
    const [y0, w0] = pontos[i]!
    const [y1, w1] = pontos[i + 1]!
    if (y <= y1) return w0 + (w1 - w0) * suave(y0, y1, y)
  }
  return pontos[pontos.length - 1]![1]
}

/** Distância, no plano ZY, de um ponto ao segmento da barra de proteção de uma porta. */
function distanciaBarraPorta(perfil: PerfilVeiculo, z: number, y: number): number {
  let minimo = Infinity
  for (const faixa of [perfil.portaDianteira, perfil.portaTraseira]) {
    // mesma diagonal que `PortaDidatica` desenha
    const z0 = faixa.zDianteiro - 0.05
    const y0 = perfil.topoSoleiraY + 0.12
    const z1 = faixa.zTraseiro + 0.05
    const y1 = perfil.cinturaY - 0.12
    const dz = z1 - z0
    const dy = y1 - y0
    const t = limitar(((z - z0) * dz + (y - y0) * dy) / (dz * dz + dy * dy), 0, 1)
    minimo = Math.min(minimo, Math.hypot(z - (z0 + dz * t), y - (y0 + dy * t)))
  }
  return minimo
}

/**
 * Lateral: intrusão na direção −(face), com perfil ao longo do comprimento,
 * perfil de altura (soleira e teto resistem), barra de proteção mais dura
 * que a chapa e vincos diagonais — não uma depressão oval.
 */
function deslocamentoLateral(
  c: CampoDeformacao,
  perfil: PerfilVeiculo,
  p: Vetor3,
  camada: CamadaDeformacao,
): Vetor3 {
  const I = c.intrusaoM
  const sx = c.sinalLateral
  const hwV = perfil.largura / 2
  const profundidade = hwV - p.x * sx
  const alcance = Math.min(I * 1.6 + 0.25, hwV * 0.95)
  const s = limitar(1 - profundidade / alcance, 0, 1)
  if (s <= 0) return ZERO

  const u = p.z - c.centroFaceM
  const hw = c.larguraM / 2
  // a chapa externa fora das portas (para-lamas, soleira, coluna B por fora)
  // acompanha com perfil mais largo e mais raso: quem entra é a porta
  const wz = camada === 'lateral'
    ? pesoTransversal(c.alvo === 'poste' ? 'outroVeiculo' : c.alvo, u, hw * 1.35, c.assimetria)
    : pesoTransversal(c.alvo, u, hw, c.assimetria)
  const wy = pesoVertical(perfil, p.y, c.alturaContatoY, c.alvo)
  let entrada = I * wz * wy * Math.pow(s, 0.8)

  const fatorCamada: Record<CamadaDeformacao, number> = {
    porta: 1,
    lateral: 0.4,
    estrutura: 0.8,
    teto: 0.5,
    rigida: 1,
    paraChoque: 0.7,
    capo: 0.35,
    tampaTraseira: 0.35,
  }
  entrada *= fatorCamada[camada]

  if (camada === 'porta') {
    // barra de proteção: resiste mais que a chapa, fica como uma nervura
    entrada *= 1 - 0.32 * Math.exp(-((distanciaBarraPorta(perfil, p.z, p.y) / 0.08) ** 2))
    // vincos diagonais partindo das bordas do contato
    const yc = c.alturaContatoY
    const a = 0.65
    const d1 = -(p.z - (c.centroFaceM + hw * 0.75)) * Math.sin(a) + (p.y - yc) * Math.cos(a)
    const d2 = -(p.z - (c.centroFaceM - hw * 0.75)) * Math.sin(-a) + (p.y - yc) * Math.cos(-a)
    entrada += I * wy * wz * 0.16 * (vinco(d1, 0.08) + vinco(d2, 0.08)) * s
    // prateleira horizontal onde a borda de cima do para-choque de quem bate dobra a porta
    if (c.alvo !== 'poste') entrada += I * wz * 0.12 * vinco(p.y - (yc + 0.17), 0.06) * s
  }
  if (camada === 'estrutura') {
    // coluna B e soleira: intrusão limitada, a cabine continua reconhecível
    const limite = c.nivel === 'severo' ? 0.3 : 0.2
    entrada = Math.min(entrada, limite)
  }

  const dx = -sx * entrada
  // a chapa é puxada para dentro do amassado: o que está ao redor converge
  const dz = -Math.sign(u) * I * 0.08 * wy * s * Math.max(0, 1 - Math.abs(u) / (hw * 2.5))
  let dy = 0
  // teto só se altera em impacto severo, e só a borda do lado atingido
  if (I > 0.28 && p.y > perfil.cinturaY) {
    dy -= (I - 0.28) * 0.45 * wz * suave(perfil.cinturaY, perfil.tetoY, p.y) * s
  }
  return vetor(dx, dy, dz)
}

/**
 * Teto: a linha do teto baixa (não uma depressão central), mais do lado que
 * encontrou o solo; as colunas acompanham e se inclinam para o outro lado.
 */
function deslocamentoTeto(
  c: CampoDeformacao,
  perfil: PerfilVeiculo,
  p: Vetor3,
  camada: CamadaDeformacao,
): Vetor3 {
  const I = c.intrusaoM
  const t = suave(perfil.cinturaY - 0.05, perfil.tetoY, p.y)
  if (t <= 0) return ZERO
  if (c.localizado) {
    const distancia = Math.hypot(p.x - c.centroFaceM, p.z - (c.centroLongitudinalM ?? 0))
    const peso = Math.max(0, 1 - distancia / Math.max(0.12, c.larguraM / 2))
    const fator = camada === 'estrutura' ? 0.85 : camada === 'porta' ? 0.35 : 1
    return vetor(0, -I * Math.pow(t, 1.3) * peso * fator, 0)
  }
  const sr = c.sinalLateral
  const hwV = perfil.largura / 2
  const xr = limitar((p.x * sr) / hwV, -1, 1)
  const capotamento = c.variante === 'capotamento'
  const wx = capotamento ? 0.5 + 0.5 * (xr + 1) / 2 : 0.15 + 0.85 * suave(-0.2, 1, xr)
  const zr = limitar(
    (p.z - perfil.teto.zTraseiro) / Math.max(perfil.teto.zDianteiro - perfil.teto.zTraseiro, 1e-6),
    -0.3,
    1.3,
  )
  // no capotamento a quina do para-brisa é a que mais cede
  const wz = capotamento ? 0.72 + 0.28 * zr : 0.85 + 0.15 * zr

  let dy = -I * Math.pow(t, 1.3) * wx * wz
  // vinco longitudinal junto à longarina do teto do lado que desce, e um transversal sobre a coluna B
  dy -= I * 0.25 * t * vinco(p.x - sr * hwV * 0.55, 0.14) * wz
  dy -= I * 0.12 * t * vinco(p.z - perfil.colunaB.z, 0.12) * wx
  // paralelogramo: o teto é empurrado para o outro lado e as colunas se inclinam
  let dx = -sr * I * (capotamento ? 0.4 : 0.25) * Math.pow(t, 1.5)
  if (!capotamento) {
    // tombamento: a borda do teto do lado apoiado entra
    dx += -sr * I * 0.5 * t * suave(0.3, 1, xr)
  }
  const fator = camada === 'estrutura' ? 0.85 : camada === 'porta' ? 0.35 : 1
  return vetor(dx * fator, dy * fator, 0)
}

function deslocamentoDoCampo(
  c: CampoDeformacao,
  perfil: PerfilVeiculo,
  p: Vetor3,
  camada: CamadaDeformacao,
): Vetor3 {
  if (c.intrusaoM <= 1e-5) return ZERO
  switch (c.regiao) {
    case 'frente':
    case 'traseira':
      return deslocamentoLongitudinal(c, perfil, p, camada)
    case 'lateralEsquerda':
    case 'lateralDireita':
      return deslocamentoLateral(c, perfil, p, camada)
    case 'teto':
      return deslocamentoTeto(c, perfil, p, camada)
  }
}

/**
 * Deslocamento de um ponto do veículo, somando todos os impactos sofridos.
 *
 * Sempre calculado a partir da posição ORIGINAL do ponto: quem aplica isto nos
 * vértices parte da geometria base a cada instante, então voltar a timeline
 * devolve exatamente a forma daquele instante — nada se acumula.
 */
export function deslocamentoNoPonto(
  estado: EstadoDeformacaoVeiculo | null | undefined,
  perfil: PerfilVeiculo,
  p: Vetor3,
  camada: CamadaDeformacao,
): Vetor3 {
  if (!estado || estado.campos.length === 0) return ZERO
  let dx = 0
  let dy = 0
  let dz = 0
  let maximo = 0
  for (const campo of estado.campos) {
    const d = deslocamentoDoCampo(campo, perfil, p, camada)
    dx += d.x
    dy += d.y
    dz += d.z
    maximo = Math.max(maximo, campo.intrusaoMaxM)
  }
  // limite global: nenhum ponto anda mais que a intrusão declarada (+10%)
  const tamanho = Math.hypot(dx, dy, dz)
  const limite = maximo * 1.1 + 0.02
  if (tamanho > limite && tamanho > 0) {
    const k = limite / tamanho
    return vetor(dx * k, dy * k, dz * k)
  }
  return vetor(dx, dy, dz)
}

/** Aplica o campo: posição final de um ponto. */
export function deformarPonto(
  estado: EstadoDeformacaoVeiculo | null | undefined,
  perfil: PerfilVeiculo,
  p: Vetor3,
  camada: CamadaDeformacao,
): Vetor3 {
  const d = deslocamentoNoPonto(estado, perfil, p, camada)
  return vetor(p.x + d.x, p.y + d.y, p.z + d.z)
}

/**
 * Degraus de intrusão por esmagamento. A malha só é reescrita quando a
 * intrusão muda de degrau: no máximo 60 reconstruções por impacto, qualquer
 * que seja a velocidade de reprodução (em 0,25× o esmagamento dura centenas
 * de quadros). 1/60 da intrusão máxima é menos de 1 cm — invisível.
 */
const DEGRAUS_INTRUSAO = 60

/** Campo amostrado a partir do descritor; nulo antes do contato. */
export function campoNoInstante(
  descritor: DescritorDeformacaoVeicular,
  tempo: number,
): CampoDeformacao | null {
  const amostra = amostrarDeformacao(descritor, tempo)
  if (!amostra.deformado || descritor.intrusaoMaxM <= 0) return null
  const degrau = Math.round((amostra.intrusaoAtualM / descritor.intrusaoMaxM) * DEGRAUS_INTRUSAO)
  if (degrau <= 0) return null
  const intrusaoM = (descritor.intrusaoMaxM * degrau) / DEGRAUS_INTRUSAO
  return {
    regiao: descritor.regiao,
    alvo: descritor.alvo,
    variante: descritor.variante ?? 'impacto',
    nivel: descritor.nivel ?? 'moderado',
    intrusaoM,
    intrusaoMaxM: descritor.intrusaoMaxM,
    progresso: amostra.progresso,
    larguraM: descritor.larguraM,
    centroFaceM: descritor.centroFaceM ?? 0,
    alturaContatoY: descritor.alturaContatoY ?? 0.52,
    sinalLateral: descritor.sinalLateral ?? -1,
    smallOverlap: Boolean(descritor.smallOverlap),
    assimetria: descritor.assimetria ?? 0,
    riscos: Boolean(descritor.riscos),
  }
}

/**
 * Estado de deformação de UM veículo num instante da timeline.
 *
 * Filtra por `veiculoId`: passar a lista inteira da cena é seguro, o dano de
 * outro veículo nunca entra aqui.
 */
export function estadoDeformacaoNoInstante(
  veiculoId: string,
  descritores: readonly (DescritorDeformacaoVeicular | null | undefined)[],
  tempo: number,
): EstadoDeformacaoVeiculo {
  const campos: CampoDeformacao[] = []
  for (const descritor of descritores) {
    if (!descritor || descritor.veiculoId !== veiculoId) continue
    const campo = campoNoInstante(descritor, tempo)
    if (campo) campos.push(campo)
  }
  return {
    veiculoId,
    campos,
    chave: campos.map((c) => `${c.regiao}:${c.intrusaoM.toFixed(4)}`).join('|'),
  }
}

export const semDeformacao = (estado: EstadoDeformacaoVeiculo | null | undefined): boolean =>
  !estado || estado.campos.length === 0

// ---------------------------------------------------------------------------
// Resposta por peça
// ---------------------------------------------------------------------------

export type EstadoVidro = 'integro' | 'trincado' | 'rompido'

/**
 * Deformação de UMA peça, pronta para a cena aplicar.
 *
 * Peças rígidas (farol, lanterna, roda, grade, retrovisor, arco do para-lama)
 * recebem translação e rotação tiradas do próprio campo no ponto de ancoragem:
 * o farol gira porque a chapa em volta dele gira. Peças de chapa deformadas
 * por vértice aparecem aqui só com o resumo (recuo, vincos), para leitura e teste.
 */
export interface DeformacaoPeca {
  pecaId: string
  translacao: [number, number, number]
  rotacao: [number, number, number]
  /** recuo ao longo da direção da carga, em metros */
  recuo: number
  /** torção em torno do eixo da carga, radianos */
  torcao: number
  /** flambagem lateral máxima, em metros (estruturas) */
  flambagem: number
  /** número de linhas de dobra visíveis na peça */
  vinco: number
  visivel: boolean
  vidro?: EstadoVidro
}

const LIMITE_ROTACAO_RAD = 0.55
const LIMITE_TRANSLACAO_RIGIDA_M = 0.35

/**
 * Translação e rotação de uma peça rígida ancorada em `ancora`.
 *
 * A rotação sai da parte antissimétrica do gradiente do campo (diferenças
 * finitas): pequena rotação local que a chapa em volta impõe à peça.
 */
export function transformacaoRigida(
  estado: EstadoDeformacaoVeiculo | null | undefined,
  perfil: PerfilVeiculo,
  ancora: Vetor3,
  camada: CamadaDeformacao = 'rigida',
): { translacao: [number, number, number]; rotacao: [number, number, number] } {
  if (semDeformacao(estado)) return { translacao: [0, 0, 0], rotacao: [0, 0, 0] }
  const h = 0.06
  const d = (dx: number, dy: number, dz: number) =>
    deslocamentoNoPonto(estado, perfil, vetor(ancora.x + dx, ancora.y + dy, ancora.z + dz), camada)
  const d0 = d(0, 0, 0)
  const ddx = (a: Vetor3, b: Vetor3) => ({
    x: (a.x - b.x) / (2 * h),
    y: (a.y - b.y) / (2 * h),
    z: (a.z - b.z) / (2 * h),
  })
  const gx = ddx(d(h, 0, 0), d(-h, 0, 0))
  const gy = ddx(d(0, h, 0), d(0, -h, 0))
  const gz = ddx(d(0, 0, h), d(0, 0, -h))
  const rx = limitar((gy.z - gz.y) / 2, -LIMITE_ROTACAO_RAD, LIMITE_ROTACAO_RAD)
  const ry = limitar((gz.x - gx.z) / 2, -LIMITE_ROTACAO_RAD, LIMITE_ROTACAO_RAD)
  const rz = limitar((gx.y - gy.x) / 2, -LIMITE_ROTACAO_RAD, LIMITE_ROTACAO_RAD)
  const l = LIMITE_TRANSLACAO_RIGIDA_M
  return {
    translacao: [limitar(d0.x, -l, l), limitar(d0.y, -l, l), limitar(d0.z, -l, l)],
    rotacao: [rx, ry, rz],
  }
}

/** Pontos de ancoragem das peças rígidas, derivados do perfil (mesmas contas dos componentes 3D). */
export function ancorasPecasRigidas(perfil: PerfilVeiculo): Record<string, Vetor3> {
  const hw = perfil.largura / 2
  const xFarol = hw - 0.16 - 0.06
  const yFarol = perfil.capoY - 0.085
  const zFarol = perfil.frenteZ - 0.07
  const xLanterna = hw - 0.18 - 0.04
  const yLanterna = perfil.volumeTraseiro === 'hatch' ? 0.72 : perfil.cinturaY - 0.04
  const zLanterna = perfil.traseiraZ + 0.06
  const xRoda = hw - perfil.larguraRoda / 2 + 0.02
  const yArco = perfil.raioRoda + raioArcoRoda(perfil) * 0.85
  const ancoras: Record<string, Vetor3> = {
    grade: vetor(0, (perfil.capoY + 0.26) / 2 + 0.06, perfil.frenteZ - 0.04),
    paraBrisa: vetor(0, (perfil.cinturaY + perfil.tetoY) / 2, (perfil.colunaA.zBase + perfil.colunaA.zTopo) / 2),
    vidroTraseiro: vetor(
      0,
      (perfil.cinturaY + perfil.tetoY) / 2,
      (perfil.colunaC.zBase + perfil.colunaC.zTopo) / 2,
    ),
  }
  for (const [sinal, sufixo] of [
    [1, 'Esquerda'],
    [-1, 'Direita'],
  ] as const) {
    ancoras[`farol${sufixo}`] = vetor(sinal * xFarol, yFarol, zFarol)
    ancoras[`lanterna${sufixo}`] = vetor(sinal * xLanterna, yLanterna, zLanterna)
    ancoras[`rodaDianteira${sufixo}`] = vetor(sinal * xRoda, perfil.raioRoda, perfil.eixoDianteiroZ)
    ancoras[`rodaTraseira${sufixo}`] = vetor(sinal * xRoda, perfil.raioRoda, perfil.eixoTraseiroZ)
    ancoras[`paralamaDianteiro${sufixo}`] = vetor(sinal * hw, yArco, perfil.eixoDianteiroZ)
    ancoras[`paralamaTraseiro${sufixo}`] = vetor(sinal * hw, yArco, perfil.eixoTraseiroZ)
    ancoras[`retrovisor${sufixo}`] = vetor(sinal * (hw + 0.085), perfil.cinturaY + 0.06, perfil.colunaA.zBase - 0.04)
    ancoras[`vidroDianteiro${sufixo}`] = vetor(
      sinal * (hw - 0.05),
      (perfil.cinturaY + perfil.tetoY) / 2 - 0.05,
      (perfil.portaDianteira.zDianteiro + perfil.portaDianteira.zTraseiro) / 2,
    )
    ancoras[`vidroTraseiro${sufixo}`] = vetor(
      sinal * (hw - 0.05),
      (perfil.cinturaY + perfil.tetoY) / 2 - 0.05,
      (perfil.portaTraseira.zDianteiro + perfil.portaTraseira.zTraseiro) / 2,
    )
  }
  return ancoras
}

const intrusaoTotal = (estado: EstadoDeformacaoVeiculo, regiao?: RegiaoDanoVeicular): number =>
  estado.campos
    .filter((c) => !regiao || c.regiao === regiao)
    .reduce((maximo, c) => Math.max(maximo, c.intrusaoM), 0)

/**
 * Estado de um vidro: trinca com a deformação da moldura; rompe quando ela
 * passa do que o vidro acompanha. O para-brisa laminado trinca na frontal
 * severa mesmo sem estar na zona esmagada (a caixa de ar empurra a base).
 */
export function estadoDoVidro(
  estado: EstadoDeformacaoVeiculo | null | undefined,
  perfil: PerfilVeiculo,
  idVidro: string,
): EstadoVidro {
  if (!estado || semDeformacao(estado)) return 'integro'
  const ancora = ancorasPecasRigidas(perfil)[idVidro]
  if (!ancora) return 'integro'
  // o vidro sente a moldura: vale a maior deformação entre porta e estrutura
  const dPorta = deslocamentoNoPonto(estado, perfil, ancora, 'porta')
  const dEstrutura = deslocamentoNoPonto(estado, perfil, ancora, 'estrutura')
  const local = Math.max(Math.hypot(dPorta.x, dPorta.y, dPorta.z), Math.hypot(dEstrutura.x, dEstrutura.y, dEstrutura.z))
  let nivel: EstadoVidro = local > 0.2 ? 'rompido' : local > 0.07 ? 'trincado' : 'integro'
  if (idVidro === 'paraBrisa') {
    const frente = intrusaoTotal(estado, 'frente')
    const teto = intrusaoTotal(estado, 'teto')
    // laminado: trinca (frontal severa ou teto que desceu), mas não se desfaz
    nivel = frente >= 0.42 || teto >= 0.12 || nivel !== 'integro' ? 'trincado' : 'integro'
  }
  if (idVidro === 'vidroTraseiro') {
    const tras = intrusaoTotal(estado, 'traseira')
    if (tras >= 0.45) nivel = 'rompido'
    else if (tras >= 0.28 && nivel === 'integro') nivel = 'trincado'
  }
  return nivel
}

/**
 * Resposta por peças: a mesma colisão gerando deformações coordenadas.
 *
 * Os ids seguem o sufixo das peças 3D (Esquerda em +X, Direita em −X, como em
 * `EstruturaVeicular` e `PortaDidatica`).
 */
export function respostaPorPecas(
  estado: EstadoDeformacaoVeiculo | null | undefined,
  perfil: PerfilVeiculo,
): Record<string, DeformacaoPeca> {
  const resposta: Record<string, DeformacaoPeca> = {}
  if (!estado || semDeformacao(estado)) return resposta
  const ancoras = ancorasPecasRigidas(perfil)
  const severo = estado.campos.some((c) => c.nivel === 'severo')

  for (const [pecaId, ancora] of Object.entries(ancoras)) {
    const vidro = pecaId.startsWith('vidro') || pecaId === 'paraBrisa'
    const faroOuLanterna = pecaId.startsWith('farol') || pecaId.startsWith('lanterna') || pecaId === 'grade'
    // farol, grade e lanterna vão presos à fáscia: acompanham o para-choque
    const { translacao, rotacao } = transformacaoRigida(estado, perfil, ancora, faroOuLanterna ? 'paraChoque' : 'rigida')
    const recuo = Math.hypot(...translacao)
    // farol/lanterna somem só no severo e quando a quina toda entrou; a grade
    // plástica se parte antes
    const visivel =
      !(faroOuLanterna && severo && recuo > 0.26) &&
      !(pecaId === 'grade' && recuo > 0.18) &&
      !(pecaId.startsWith('retrovisor') && recuo > 0.14)
    resposta[pecaId] = {
      pecaId,
      translacao: vidro ? [0, 0, 0] : translacao,
      rotacao: vidro ? [0, 0, 0] : rotacao,
      recuo,
      torcao: rotacao[2],
      flambagem: 0,
      vinco: 0,
      visivel,
      vidro: vidro ? estadoDoVidro(estado, perfil, pecaId) : undefined,
    }
  }

  // resumos das chapas deformadas por vértice
  const resumo = (pecaId: string, ponto: Vetor3, camada: CamadaDeformacao, vincos: number) => {
    const d = deslocamentoNoPonto(estado, perfil, ponto, camada)
    const recuo = Math.hypot(d.x, d.y, d.z)
    if (recuo < 1e-4) return
    resposta[pecaId] = {
      pecaId,
      translacao: [0, 0, 0],
      rotacao: [0, 0, 0],
      recuo,
      torcao: 0,
      flambagem: 0,
      vinco: vincos,
      visivel: true,
    }
  }
  const frente = estado.campos.find((c) => c.regiao === 'frente')
  const tras = estado.campos.find((c) => c.regiao === 'traseira')
  resumo('paraChoqueDianteiro', vetor(frente?.centroFaceM ?? 0, 0.5, perfil.frenteZ), 'paraChoque', 0)
  resumo('capo', vetor(frente?.centroFaceM ?? 0, perfil.capoY, perfil.frenteZ - 0.1), 'capo', VINCOS_CAPO.length)
  resumo('paraChoqueTraseiro', vetor(tras?.centroFaceM ?? 0, 0.5, perfil.traseiraZ), 'paraChoque', 0)
  resumo('tampaTraseira', vetor(tras?.centroFaceM ?? 0, perfil.cinturaY - 0.1, perfil.traseiraZ + 0.06), 'tampaTraseira', VINCOS_TAMPA.length)
  resumo('teto', vetor(0, perfil.tetoY, (perfil.teto.zDianteiro + perfil.teto.zTraseiro) / 2), 'teto', 2)
  for (const [sinal, sufixo] of [
    [1, 'Esquerda'],
    [-1, 'Direita'],
  ] as const) {
    const yPorta = (perfil.topoSoleiraY + perfil.cinturaY) / 2
    const x = sinal * perfil.largura / 2
    resumo(`portaDianteira${sufixo}`, vetor(x, yPorta, (perfil.portaDianteira.zDianteiro + perfil.portaDianteira.zTraseiro) / 2), 'porta', 2)
    resumo(`portaTraseira${sufixo}`, vetor(x, yPorta, (perfil.portaTraseira.zDianteiro + perfil.portaTraseira.zTraseiro) / 2), 'porta', 2)
    const colunaB = deslocamentoNoPonto(estado, perfil, vetor(x * 0.97, perfil.cinturaY - 0.1, perfil.colunaB.z), 'estrutura')
    if (Math.hypot(colunaB.x, colunaB.y, colunaB.z) > 1e-4) {
      resposta[`colunaB${sufixo}`] = {
        pecaId: `colunaB${sufixo}`,
        translacao: [colunaB.x, colunaB.y, colunaB.z],
        rotacao: [0, 0, 0],
        recuo: Math.hypot(colunaB.x, colunaB.y, colunaB.z),
        torcao: 0,
        flambagem: 0,
        vinco: 0,
        visivel: true,
      }
    }
    // longarina: flambagem = desvio lateral máximo ao longo da zona de absorção
    let flambagem = 0
    let recuo = 0
    const xLong = sinal * (perfil.largura / 2 - 0.3)
    for (let i = 0; i <= 8; i += 1) {
      const z = perfil.zonaAbsorcaoDianteira.zTraseiro + (perfil.frenteZ - perfil.zonaAbsorcaoDianteira.zTraseiro) * (i / 8)
      const d = deslocamentoNoPonto(estado, perfil, vetor(xLong, perfil.pisoY, z), 'estrutura')
      flambagem = Math.max(flambagem, Math.abs(d.x))
      recuo = Math.max(recuo, -d.z)
    }
    if (recuo > 1e-4) {
      resposta[`longarina${sufixo}`] = {
        pecaId: `longarina${sufixo}`,
        translacao: [0, 0, 0],
        rotacao: [0, 0, 0],
        recuo,
        torcao: 0,
        flambagem,
        vinco: 0,
        visivel: true,
      }
    }
  }
  return resposta
}

